import { useEffect, useRef, useState } from "react";
import { InputOTP } from "@/shared/components/otp-input";
import { useCountdown } from "./useCountdown";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import {
  OTP_LENGTH,
  OTP_VALIDITY_SECONDS,
  OTP_EXPIRY_WARNING_SECONDS,
  saneOtpSeconds,
} from "./otpConfig";
import { toastError, toastSuccess, toastWarning } from "@/shared/lib/toast";
import { getAuthFlowState, updateAuthFlowState } from "./authFlowState";
import { AuthHeading } from "@/shared/components/AuthHeading";

// Countdowns are seeded from absolute timestamps persisted in sessionStorage, not
// hardcoded durations — otherwise a page refresh would restart both timers from
// scratch instead of resuming from the real server-side deadline.
function remainingSecondsUntil(timestamp: number | undefined): number | null {
  if (!timestamp) return null;
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
}

function initialValidity(email: string): { seconds: number; expiresAt: number } {
  const stored = getAuthFlowState();
  if (stored?.email === email && stored.otpExpiresAt) {
    const remaining = remainingSecondsUntil(stored.otpExpiresAt)!;
    // A stored expiry further out than a code can ever last is bogus — start a
    // fresh window instead of counting down from it.
    if (remaining <= OTP_VALIDITY_SECONDS) return { seconds: remaining, expiresAt: stored.otpExpiresAt };
  }
  return { seconds: OTP_VALIDITY_SECONDS, expiresAt: Date.now() + OTP_VALIDITY_SECONDS * 1000 };
}

function initialRateLimit(email: string): number {
  const stored = getAuthFlowState();
  if (stored?.email === email) return remainingSecondsUntil(stored.resendBlockedUntil) ?? 0;
  return 0;
}

interface OtpActionResult {
  success: boolean;
  error?: string;
  code?: string;
  retryAfterSeconds?: number;
  // Only populated by the password-reset flow's onVerify (see VerifyResetOtpPage) —
  // ignored by callers (e.g. signup verification) that don't need it.
  resetToken?: string;
  // Only populated by the signup-claim flow's onVerify (see VerifyOtpPage) —
  // ignored by callers (e.g. password reset) that don't need it.
  wasClaim?: boolean;
  creatorName?: string | null;
}

interface OtpPanelProps {
  email: string;
  title: React.ReactNode;
  subtitle: string;
  onVerify: (otp: string) => Promise<OtpActionResult>;
  onResend: () => Promise<OtpActionResult>;
  onVerified: (otp: string, resetToken?: string, wasClaim?: boolean, creatorName?: string | null) => void;
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function OtpPanel({ email, title, subtitle, onVerify, onResend, onVerified }: OtpPanelProps) {
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const [{ seconds: initialValiditySeconds, expiresAt: initialExpiresAt }] = useState(() => initialValidity(email));
  const [initialRateLimitSeconds] = useState(() => initialRateLimit(email));

  const validity = useCountdown(initialValiditySeconds);
  const rateLimitCountdown = useCountdown(initialRateLimitSeconds);

  // Persist the freshly-computed expiry once on mount so it survives the next refresh too.
  useEffect(() => {
    updateAuthFlowState(email, { otpExpiresAt: initialExpiresAt });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expired = validity.secondsLeft <= 0;
  const showExpiryWarning = !expired && validity.secondsLeft <= OTP_EXPIRY_WARNING_SECONDS;
  // Resend only unlocks once the current code has actually expired — a code that's
  // still valid shouldn't be discardable by an early resend.
  const canResend = expired;

  // Fire the expiry warning toast once, on the moment the countdown crosses the threshold.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (showExpiryWarning && !warnedRef.current) {
      warnedRef.current = true;
      toastWarning("Your code is about to expire. Enter it now or resend a new one.");
    }
    if (!showExpiryWarning) warnedRef.current = false;
  }, [showExpiryWarning]);

  const handleVerify = async () => {
    if (otp.length !== OTP_LENGTH || expired || verifying || resending) return;
    setVerifying(true);
    const result = await onVerify(otp);
    setVerifying(false);
    if (result.success) {
      onVerified(otp, result.resetToken, result.wasClaim, result.creatorName);
      return;
    }
    // Our own countdown is the source of truth for expiry — if it still shows time
    // left, a backend OTP_EXPIRED really means the code was wrong, not expired.
    if (result.code === "OTP_EXPIRED" && validity.secondsLeft > 0) {
      toastError("Incorrect code. Please check and try again.");
      return;
    }
    if (result.code === "OTP_EXPIRED") validity.reset(0);
    toastError(result.error ?? "Unable to verify code.");
  };

  const handleResend = async () => {
    if (!canResend || rateLimitCountdown.secondsLeft > 0 || resending || verifying) return;
    setResending(true);
    const result = await onResend();
    setResending(false);
    if (result.success) {
      setOtp("");
      validity.reset(OTP_VALIDITY_SECONDS);
      updateAuthFlowState(email, {
        otpExpiresAt: Date.now() + OTP_VALIDITY_SECONDS * 1000,
        resendBlockedUntil: undefined,
      });
      toastSuccess("A new OTP has been sent successfully. Please check your email.");
    } else if (result.code === "TOO_MANY_ATTEMPTS" && result.retryAfterSeconds) {
      rateLimitCountdown.reset(result.retryAfterSeconds);
      updateAuthFlowState(email, { resendBlockedUntil: Date.now() + result.retryAfterSeconds * 1000 });
    } else {
      toastError(result.error ?? "Unable to resend code.");
    }
  };

  return (
    <div className="w-full">
      <AuthHeading title={title} subtitle={subtitle} />

      <h3 className="!mt-12 !mb-0 text-center text-lg font-semibold leading-none text-[#0A2947] sm:!mt-14">OTP</h3>
      <p className="!mt-3 !mb-0 text-center text-sm text-[#9CA3AF]">
        Enter The {OTP_LENGTH}-Digit Verification Code Sent To Your Email.
      </p>

      {/* Boxes start empty (no placeholder digits); a filled box turns the
          platform red at 50% with a white digit. */}
      <div className="mt-10 flex justify-center sm:mt-[56px]">
        <InputOTP
          length={OTP_LENGTH}
          value={otp}
          onChange={setOtp}
          disabled={expired || verifying || resending}
          containerClassName={[
            "!gap-3 sm:!gap-5",
            "[&_.ant-otp-input]:!h-[60px] [&_.ant-otp-input]:!w-[42px] sm:[&_.ant-otp-input]:!h-[67px] sm:[&_.ant-otp-input]:!w-[46px]",
            "[&_.ant-otp-input]:!rounded-[20px] [&_.ant-otp-input]:!border-[#E5E7EB] [&_.ant-otp-input]:!bg-white [&_.ant-otp-input]:!p-0",
            "[&_.ant-otp-input]:!text-xl [&_.ant-otp-input]:!text-[#F07C93] [&_.ant-otp-input]:!shadow-[0_1px_2px_rgba(17,24,39,0.04)]",
            "[&_.ant-otp-input:focus]:!border-[#E4002B]/50 [&_.ant-otp-input:focus]:!shadow-none",
            // antd selects a box's digit when it gets focus; hide that blue selection highlight.
            "[&_.ant-otp-input::selection]:!bg-transparent [&_.ant-otp-input::selection]:!text-inherit",
            "[&_.ant-otp-input:not([value=''])]:!border-transparent [&_.ant-otp-input:not([value=''])]:!bg-[#E4002B]/50 [&_.ant-otp-input:not([value=''])]:!text-white",
          ].join(" ")}
        />
      </div>

      <AuthSubmitButton
        htmlType="button"
        onClick={handleVerify}
        loading={verifying}
        disabled={expired || resending || otp.length !== OTP_LENGTH}
        className="!mt-10 !h-[44px] !py-0 !text-[15px] !font-medium sm:!mt-[43px]"
      >
        Verify
      </AuthSubmitButton>

      <p className="!mt-5 !mb-0 text-center text-[13px] text-[#6B7280]">
        {expired ? (
          "Code expired."
        ) : (
          <>
            Code expires in{" "}
            <span className="font-semibold text-[#111827]">{formatTime(validity.secondsLeft)}</span>
          </>
        )}
      </p>

      <p className="!mt-1.5 !mb-0 text-center text-[13px] text-[#6B7280]">
        {rateLimitCountdown.secondsLeft > 0 ? (
          <>
            You've reached the maximum number of attempts.
            <br />
            Try again in{" "}
            <span className="font-semibold text-[#0A2947]">{formatTime(rateLimitCountdown.secondsLeft)}</span>
          </>
        ) : (
          <>
            Didn't get the code?{" "}
            <Button
              variant="link"
              onClick={handleResend}
              loading={resending}
              // Text sits before this link, so its spinner floats on the right.
              spinner="end"
              disabled={verifying || !canResend}
              className="!h-auto !p-0 !font-semibold !text-[#000000] hover:!text-[#000000] hover:!underline disabled:!no-underline"
            >
              Resend OTP
            </Button>
          </>
        )}
      </p>
    </div>
  );
}
